import { ButtonLink, Stack } from 'component-library';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { BenchmarkType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { CapsuleTrend } from '@/components';
import { kgToTonneFormatter } from '@/components/Charts/utils';
import { NCCardSectionHeader, printLatestDate, printNthYearDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePathSet } from '@/hooks/usePathSet';
import { useUIStyle } from '@/hooks/useUIStyle';
import { getBenchmarksFromFacts, useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { assertBenchmarkType } from '@/utils/assert';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';
import { formatFactKilogramsToTonnes } from '@/utils/formatting';

export type CarbonStatsTileProps = {
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  oneYearUpliftFact: PlotReportFactElement<number | null> | undefined;
  oneYearPctChangeFact: PlotReportFactElement<number | null> | undefined;
  benchmarkFact: {
    high: PlotReportFactElement<number | null> | undefined;
    moderate: PlotReportFactElement<number | null> | undefined;
    low: PlotReportFactElement<number | null> | undefined;
  };
};

export const CarbonStatsTile = ({
  latestAnalysisFact,
  oneYearUpliftFact,
  oneYearPctChangeFact,
  benchmarkFact,
}: CarbonStatsTileProps) => {
  const { t } = useTranslation();
  const pathSet = usePathSet();
  const pathname = useLocation();
  const plotGroupId = usePlotGroupId();

  const carbonBgLatestAnalysis = formatFactKilogramsToTonnes(latestAnalysisFact);
  const cbgBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: benchmarkFact.high,
    moderateBenchmarkFact: benchmarkFact.moderate,
    lowBenchmarkFact: benchmarkFact.low,
    formatter: kgToTonneFormatter,
  });
  const cbgBenchmarkType = useBenchmarkForFact(cbgBenchmarks, carbonBgLatestAnalysis?.value);
  assertBenchmarkType(cbgBenchmarkType, 'CarbonStatsTile');
  assertBenchmarkType(cbgBenchmarkType, 'CarbonStatsTile', BenchmarkType.unchanged);
  assertBenchmarkType(cbgBenchmarkType, 'CarbonStatsTile', BenchmarkType.stable);
  assertBenchmarkType(cbgBenchmarkType, 'CarbonStatsTile', BenchmarkType.alerted);
  assertBenchmarkType(cbgBenchmarkType, 'CarbonStatsTile', BenchmarkType.very_high);

  const carbonBg1yChange = formatFactKilogramsToTonnes(oneYearUpliftFact);

  let carbonStorageBgTrendDisplay = `${useDisplayNumber(oneYearPctChangeFact?.value ?? 0, {
    signDisplay: 'always',
  })} %`;
  carbonStorageBgTrendDisplay = !isNullish(oneYearPctChangeFact?.value) ? carbonStorageBgTrendDisplay : '-';

  const hideImpactProjectionsButton = useUIStyle(OrganizationUIStyle.CONSERVATION);

  const lastMeasuredDate = printLatestDate([carbonBgLatestAnalysis?.measured_at]);
  const carbonBg1yChangeInitialMeasuredAt = lastMeasuredDate;
  const carbonBg1yChangePreviousMeasuredAt = printNthYearDate([carbonBg1yChange?.measured_at], -1);

  return (
    <Stack className='justify-center gap-8 p-6 lg:gap-4 lg:p-8 lg:pr-2' data-testid='carbon-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.carbon.labels.belowTheGroundCarbonStorage')}
          supplementaryInfo={`${t('shared.ncaDetail.details.labels.updated')} ${lastMeasuredDate}`}
          popover={{
            title: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.title'),
            body: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
          }}
        />
        <Stack direction='row' className='flex-wrap gap-16'>
          <NCFactCapsule
            label={t('shared.ncaDetail.details.labels.latestAnalysis')}
            fact={carbonBgLatestAnalysis}
            trend={
              typeof oneYearPctChangeFact?.value === 'number' && (
                <CapsuleTrend variant={oneYearPctChangeFact.value < 0 ? 'negative' : 'positive'}>
                  {carbonStorageBgTrendDisplay}
                </CapsuleTrend>
              )
            }
            capsuleIconProps={{
              icon: getNCChangeIcon(carbonBgLatestAnalysis),
              variant: getNCChangeIconVariant(carbonBgLatestAnalysis),
            }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
              body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: lastMeasuredDate }),
              popoverTriggerProps: {
                className: 'text-xs',
              },
            }}
          />
          {!isNullish(carbonBg1yChange?.value) && (
            <NCFactCapsule
              label={t('shared.ncaDetail.details.labels.oneYearChange')}
              fact={carbonBg1yChange}
              capsuleIconProps={{
                icon: getNCChangeIcon(carbonBg1yChange),
                variant: getNCChangeIconVariant(carbonBg1yChange),
              }}
              infoPopoverProps={{
                title: t('shared.ncaDetail.details.carbon.explainers.oneYearChange.title'),
                body: t('shared.ncaDetail.details.carbon.explainers.oneYearChange.body', {
                  date: carbonBg1yChangeInitialMeasuredAt,
                  previousDate: carbonBg1yChangePreviousMeasuredAt,
                }),
                popoverTriggerProps: {
                  'data-testid': 'cbg-since-previous-analysis-tooltip',
                },
              }}
            />
          )}
        </Stack>
        {cbgBenchmarkType && (
          <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
            <BenchmarkPill benchmarkType={cbgBenchmarkType} />
            <span className='typography-body1Bold'>
              {t('shared.ncaDetail.details.carbon.explainers.cbgBenchmark.title')}
            </span>
            <Trans
              i18nKey={`shared.ncaDetail.details.carbon.explainers.cbgBenchmark.${cbgBenchmarkType}`}
              components={{
                p: <p className='typography-body1 text-text-secondary' />,
              }}
            />
            {/* TODO: TEMP FIX - Remove 'false &&' when Imapct Projections is fixed */}
            {/* eslint-disable-next-line sonarjs/no-redundant-boolean */}
            {false && !hideImpactProjectionsButton && (
              <ButtonLink
                to={buildPath(pathSet.impactProjections, { queryParams: { plotGroupId } })}
                state={{
                  previousPath: pathname,
                }}
                variant='outline'
                className='w-fit'
              >
                {t('shared.ncaDetail.details.carbon.explainers.cbgBenchmark.buttonLabel')}
              </ButtonLink>
            )}
          </Stack>
        )}
      </div>
    </Stack>
  );
};
